import { SettingsConsts, XhrConsts } from 'client/util/Constants';
import MspState from 'client/components/models/MspState';
import PageNavigationUtil from 'client/util/PageNavigationUtil';
import axios from 'axios';
import rbacUtils from 'client/util/rbacUtils';

const { xCSRFToken, xOrgIdHeader, xTotalCount } = XhrConsts;
const { isMspAdminErrMsg, isMspStatusCode } = SettingsConsts;

// csrfPromise allows all requests needing csrf to get the token
let csrfPromise;

// The setOrgIdHeader & setCsrfHeader interceptors
// re-implement Api.js::initialize
// in order to avoid needing to explicitly call
// initialize for each request
export function setOrgIdHeader(config) {
  /* eslint-disable no-param-reassign */
  if (MspState.orgId) {
    config.headers[xOrgIdHeader] = MspState.orgId;
  } else {
    delete config.headers[xOrgIdHeader];
  }

  return config;
}

export async function setCsrfHeader(config) {
  if (config.method !== 'get') {
    const { data: csrfToken } = await csrfPromise;
    // eslint-disable-next-line no-param-reassign
    config.headers.common[xCSRFToken] = csrfToken;
  }

  return config;
}

export function addCollectionTotal(res) {
  res.totalCount = res.headers[xTotalCount];

  return res;
}

export function interceptor403errors(error) {
  const outPut = Promise.reject(error);

  if (error.request) {
    // Let the error continue if the user is command runner and not on the commands page
    if (rbacUtils.testForCommandRunner(error.request)) {
      return outPut;
    }
  }

  if (!rbacUtils.pollBulkActionIsFinish()) {
    return outPut;
  }

  if (error.response) {
    rbacUtils.handleForbidden403(error.response.status);
  }

  return outPut;
}

/**
 * Add a response interceptor for this endpoint - if the admin logging in is
 * part of an MSP, the '/api/settings' endpoint returns a 401 with a message
 * instead of the settings.
 */
export function interceptorMspError(error) {
  if (error && error.response) {
    if (error.response.status === isMspStatusCode
      && error.response.data.message !== isMspAdminErrMsg) {
      PageNavigationUtil.handle401();
    } else {
      throw error;
    }
  } else if (error && error.response === undefined) {
    throw error;
  }
}

export function setupInterceptors(axiosInst) {
  // Added optional chaining to fix test failures
  // caused by external mocks not implementing axios.create
  /* eslint-disable no-unused-expressions */
  axiosInst?.interceptors?.request.use(setOrgIdHeader);
  axiosInst?.interceptors?.request.use(setCsrfHeader);
  axiosInst?.interceptors?.response.use(addCollectionTotal, interceptor403errors);
  axiosInst?.interceptors?.response.use(response => response, interceptorMspError);
}

// This should only be called once and by the initializationHandler.
export function setupAxios() {
  // setup the csrfPromise for all API calls to use. This function doesn't wait for the
  // promise to resolve, instead any API that needs it will await for it.
  csrfPromise = axios.get('/csrf');

  axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

  setupInterceptors(axios);
}
